import { whiteboardTextBox } from './geometry.ts';
import type { Color, NodeData, ParagraphFormat, TextRun } from './types.ts';
import { DEFAULT_FONT } from './defaults.ts';
import type { ShapedText, TextShaper } from './shaper.ts';
import { RTL_CHARS, bidiLevels, paragraphLevel, visualRuns } from './bidi.ts';

/** Measures the advance width of a string in a CSS font. Implemented by the host (browser canvas, node canvas, ...). */
export interface TextMeasurer {
  measure(text: string, font: string): number;
}

/** Fallback measurer with rough average glyph widths (used when no canvas is available). */
export const approximateMeasurer: TextMeasurer = {
  measure(text, font) {
    const size = parseFloat(font.match(/(\d+(?:\.\d+)?)px/)?.[1] ?? '16');
    const bold = /\b([6-9]00|bold)\b/.test(font);
    let w = 0;
    for (const ch of text) {
      if (ch === ' ') w += 0.28;
      else if (/[il.,'!|:;]/.test(ch)) w += 0.26;
      else if (/[mwMW@]/.test(ch)) w += 0.85;
      else if (/[A-Z]/.test(ch)) w += 0.66;
      else if (/[0-9]/.test(ch)) w += 0.58;
      else w += 0.54;
    }
    return w * size * (bold ? 1.05 : 1);
  },
};

let measurer: TextMeasurer = approximateMeasurer;

/**
 * HarfBuzz shaping (see shaper.ts). When set, text is measured from shaped glyph advances and
 * renderers draw glyph outlines; the measurer above is only the fallback for fonts or
 * characters the shaper doesn't have.
 */
let shaper: (TextShaper & { invalidate?(): void }) | null = null;
export function setTextShaper(s: (TextShaper & { invalidate?(): void }) | null) {
  shaper = s;
  invalidateTextMetrics();
}
export function getTextShaper() {
  return shaper;
}

const FONT_RE = /^(italic )?(?:small-caps )?(\d+) ([\d.]+)px (.+), sans-serif$/;
const parsed = new Map<string, { family: string; weight: number; size: number; italic: boolean } | null>();
/** Family, weight, size and style back from a CSS font made by cssFont(). */
export function parseCssFont(font: string) {
  let p = parsed.get(font);
  if (p === undefined) {
    const m = FONT_RE.exec(font);
    p = m ? { italic: !!m[1], weight: Number(m[2]), size: Number(m[3]), family: m[4].replace(/^"|"$/g, '').replace(/__ff_\w+$/, '') } : null;
    if (parsed.size > 2000) parsed.clear();
    parsed.set(font, p);
  }
  return p;
}

let fallbackFamilies = '';
/**
 * Families the host's own text engine tries for characters no shaped font has (emoji, CJK…).
 * Browsers fall back by themselves; headless canvases need the list spelled out.
 */
export function setFallbackFamilies(families: string[]) {
  fallbackFamilies = families.map((f) => `"${f}"`).join(', ');
  invalidateTextMetrics();
}
/** A cssFont() font with the fallback families added (for the host's text engine only). */
export function withFallbacks(font: string): string {
  return fallbackFamilies ? font.replace(/, sans-serif$/, `, sans-serif, ${fallbackFamilies}`) : font;
}

const NO_FEATURES: Record<string, boolean> = {};
/** A run shaped with HarfBuzz, or null when the shaper can't (no shaper, unknown font, still loading). */
export function shapeRun(text: string, font: string, fx?: GlyphFeatures): ShapedText | null {
  if (!shaper || !text) return null;
  const f = parseCssFont(font);
  if (!f) return null;
  return shaper.shape(text, f.family, f.weight, f.italic, f.size, fx?.ot ?? NO_FEATURES, (s) => measurer.measure(s, withFallbacks(font)));
}

/**
 * Code points before each UTF-16 offset (letter spacing is added per character). Right-to-left
 * runs count from the other end, since their first character is drawn at the right.
 */
export function codePointsBefore(text: string, rtl = false): Uint32Array {
  const out = new Uint32Array(text.length + 1);
  let n = 0;
  for (let i = 0; i < text.length; i++, n++) {
    out[i] = n;
    const c = text.charCodeAt(i);
    // a surrogate pair is one character
    if (c >= 0xd800 && c <= 0xdbff && i + 1 < text.length) out[++i] = n;
  }
  out[text.length] = n;
  if (rtl) for (let i = 0; i <= text.length; i++) out[i] = Math.max(0, n - 1 - out[i]);
  return out;
}

/**
 * SVG path data for a shaped run with its origin (baseline start) at x, y, or null when any of
 * it can't be shaped (exports then fall back to <text>).
 */
export function shapedPathData(text: string, font: string, ls: number, fx: GlyphFeatures | undefined, x: number, y: number): string | null {
  const sh = shapeRun(text, font, fx);
  if (!sh || sh.fallback.length) return null;
  const cps = ls ? codePointsBefore(text, sh.rtl) : null;
  const r = (v: number) => Math.round(v * 100) / 100;
  let d = '';
  for (const g of sh.glyphs) {
    const o = shaper!.outline(g.face, g.gid);
    if (!o) continue;
    const ox = x + g.x + (cps ? ls * cps[g.cluster] : 0),
      oy = y + g.y,
      k = g.size,
      sk = g.skew;
    const X = (px: number, py: number) => r(ox + (px - sk * py) * k);
    const Y = (py: number) => r(oy + py * k);
    for (let i = 0; i < o.length; ) {
      switch (o[i]) {
        case 0:
          d += `M${X(o[i + 1], o[i + 2])} ${Y(o[i + 2])}`;
          i += 3;
          break;
        case 1:
          d += `L${X(o[i + 1], o[i + 2])} ${Y(o[i + 2])}`;
          i += 3;
          break;
        case 2:
          d += `Q${X(o[i + 1], o[i + 2])} ${Y(o[i + 2])} ${X(o[i + 3], o[i + 4])} ${Y(o[i + 4])}`;
          i += 5;
          break;
        case 3:
          d += `C${X(o[i + 1], o[i + 2])} ${Y(o[i + 2])} ${X(o[i + 3], o[i + 4])} ${Y(o[i + 4])} ${X(o[i + 5], o[i + 6])} ${Y(o[i + 6])}`;
          i += 7;
          break;
        default:
          d += 'Z';
          i += 1;
      }
    }
  }
  return d;
}
export function setTextMeasurer(m: TextMeasurer) {
  measurer = m;
  cache.clear();
  cellCache.clear();
}
/**
 * Hosts whose canvas ignores "small-caps" in the font (e.g. headless Skia) turn this on:
 * lowercase letters are then measured and drawn as capitals at 70%.
 */
let synthCaps = false;
export function setSyntheticSmallCaps(on: boolean) {
  synthCaps = on;
  cache.clear();
}
const SC = 0.7;
/** Split text into [run, font] pairs for synthetic small caps (identity otherwise). */
export function smallCapsRuns(text: string, font: string): [string, string][] {
  if (!synthCaps || !font.includes('small-caps ')) return [[text, font]];
  const plain = font.replace('small-caps ', '');
  const small = plain.replace(/(\d+(?:\.\d+)?)px/, (_, n) => `${Math.round(Number(n) * SC * 100) / 100}px`);
  const out: [string, string][] = [];
  for (const seg of text.match(/\p{Ll}+|[^\p{Ll}]+/gu) ?? []) out.push(/\p{Ll}/u.test(seg) ? [seg.toUpperCase(), small] : [seg, plain]);
  return out;
}

export function getTextMeasurer(): TextMeasurer {
  return measurer;
}

const cache = new Map<string, number>();
function measure(text: string, font: string): number {
  const key = font + '\u0000' + text;
  let w = cache.get(key);
  if (w === undefined) {
    w = synthCaps && font.includes('small-caps ') ? smallCapsRuns(text, font).reduce((a, [t, f]) => a + measurer.measure(t, f), 0) : measurer.measure(text, font);
    if (cache.size > 50000) cache.clear();
    cache.set(key, w);
  }
  return w;
}

export const ASCENT = 0.96875;
export const DESCENT = 0.2421875;

export interface TextStyle {
  fontFamily: string;
  fontSize: number;
  fontWeight: number;
  italic: boolean;
  lineHeight: number; // px
  letterSpacing: number; // px
  textAlign: 'LEFT' | 'CENTER' | 'RIGHT' | 'JUSTIFIED';
  verticalAlign: 'TOP' | 'CENTER' | 'BOTTOM';
  textCase: 'ORIGINAL' | 'UPPER' | 'LOWER' | 'TITLE';
  decoration: 'NONE' | 'UNDERLINE' | 'STRIKETHROUGH';
  /** small-caps via the font (real OpenType smcp); all small caps also lowers capitals (c2sc) */
  caps: 'NORMAL' | 'SMALL_CAPS' | 'ALL_SMALL_CAPS';
  features: GlyphFeatures;
  /** Every other OpenType feature as CSS font-feature-settings, e.g. '"frac" 1, "ss01" 1' ('' when none). */
  featureCss: string;
}

/**
 * Hosts that can load a font with feature settings (browsers: FontFace featureSettings) return a
 * family name for that variant, or null while it loads / when unsupported.
 */
export type FeatureFontResolver = (family: string, weight: number, italic: boolean, featureCss: string) => string | null;
let featureResolver: FeatureFontResolver | null = null;
export function setFeatureFontResolver(r: FeatureFontResolver | null) {
  featureResolver = r;
  cache.clear();
}
/** Clear measurements after a feature font finished loading. */
export function invalidateTextMetrics() {
  cache.clear();
  cellCache.clear();
  shaper?.invalidate?.();
}

/** CSS font-feature-settings for a node's features (handled-by-layout ones included, for export). */
export function featureSettingsCss(f?: Record<string, boolean>): string {
  if (!f) return '';
  return Object.entries(f)
    .filter(([k]) => /^[A-Z0-9]{4}$/i.test(k))
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `"${k.toLowerCase()}" ${v ? 1 : 0}`)
    .join(', ');
}

/** Features the layout applies itself (canvas has no font-feature-settings). */
export interface GlyphFeatures {
  /** tabular figures: every digit takes the widest digit's advance */
  tnum?: boolean;
  /** false: no ligatures (glyphs are measured and drawn one by one) */
  liga?: boolean;
  /** Every OpenType feature by lowercase tag, for the shaper (which applies them all itself). */
  ot?: Record<string, boolean>;
}

export function textStyle(n: Partial<NodeData>): TextStyle {
  const fontSize = n.fontSize ?? 16;
  return {
    fontFamily: n.fontFamily || DEFAULT_FONT,
    fontSize,
    fontWeight: n.fontWeight ?? 400,
    italic: !!n.italic,
    lineHeight: n.lineHeight && n.lineHeight > 0 ? n.lineHeight : Math.round(fontSize * 1.21 * 100) / 100,
    letterSpacing: n.letterSpacing ?? 0,
    textAlign: n.textAlign ?? 'LEFT',
    verticalAlign: n.verticalAlign ?? 'TOP',
    textCase: n.textCase ?? 'ORIGINAL',
    decoration: n.textDecoration ?? 'NONE',
    caps: n.openTypeFeatures?.C2SC ? 'ALL_SMALL_CAPS' : n.openTypeFeatures?.SMCP ? 'SMALL_CAPS' : 'NORMAL',
    features: { tnum: !!n.openTypeFeatures?.TNUM, liga: n.openTypeFeatures?.LIGA !== false, ot: shaperFeatures(n.openTypeFeatures) },
    // small caps go through the font shorthand; the rest needs a font variant
    featureCss: featureSettingsCss(Object.fromEntries(Object.entries(n.openTypeFeatures ?? {}).filter(([k]) => k !== 'SMCP' && k !== 'C2SC'))),
  };
}

const otCache = new Map<string, Record<string, boolean>>();
/** Lowercase-tag features for the shaper; small caps also need smcp for the lowercased text. */
function shaperFeatures(f?: Record<string, boolean>): Record<string, boolean> {
  if (!f) return NO_FEATURES;
  const key = JSON.stringify(f);
  let o = otCache.get(key);
  if (!o) {
    o = {};
    for (const [k, v] of Object.entries(f)) if (/^[A-Z0-9 ]{4}$/i.test(k)) o[k.toLowerCase()] = v;
    if (o.c2sc) o.smcp = true;
    otCache.set(key, o);
  }
  return o;
}

export function cssFont(s: Pick<TextStyle, 'italic' | 'fontWeight' | 'fontSize' | 'fontFamily'> & { caps?: TextStyle['caps']; featureCss?: string }): string {
  const family = (s.featureCss && featureResolver?.(s.fontFamily, s.fontWeight, s.italic, s.featureCss)) || s.fontFamily;
  const fam = /[\s,]/.test(family) && !family.includes('"') ? `"${family}"` : family;
  return `${s.italic ? 'italic ' : ''}${s.caps && s.caps !== 'NORMAL' ? 'small-caps ' : ''}${s.fontWeight} ${s.fontSize}px ${fam}, sans-serif`;
}

/** Case transform plus the lowering that all-small-caps needs. */
function transformText(text: string, s: Pick<TextStyle, 'textCase' | 'caps'>): string {
  const t = applyCase(text, s.textCase);
  return s.caps === 'ALL_SMALL_CAPS' ? t.toLowerCase() : t;
}

const DIGIT = /[0-9]/;
const cellCache = new Map<string, number>();
/** Advance of a tabular figure in this font: the widest digit. */
export function tabularCell(font: string): number {
  let w = cellCache.get(font);
  if (w === undefined) {
    w = 0;
    for (let d = 0; d <= 9; d++) w = Math.max(w, measure(String(d), font));
    cellCache.set(font, w);
  }
  return w;
}

export function applyCase(text: string, c: TextStyle['textCase']): string {
  switch (c) {
    case 'UPPER':
      return text.toUpperCase();
    case 'LOWER':
      return text.toLowerCase();
    case 'TITLE':
      return text.replace(/\b\p{L}/gu, (m) => m.toUpperCase());
    default:
      return text;
  }
}

export interface TextPiece {
  text: string;
  /** index into TextLayout.styles */
  style: number;
  x: number;
  width: number;
  /** Bidirectional lines: where the piece starts in the line's text (pieces are in visual order). */
  start?: number;
  /** Right-to-left piece: its first character is drawn at the right. */
  rtl?: boolean;
}

export interface PieceStyle {
  font: string;
  fontSize: number;
  letterSpacing: number;
  features?: GlyphFeatures;
  decoration: TextStyle['decoration'];
  color?: Color;
  link?: string;
}

export interface TextLine {
  text: string;
  /** List marker drawn in the indent (first line of a list paragraph). */
  marker?: string;
  markerX?: number;
  /** Present for rich text: differently styled pieces, x relative to the line start. */
  pieces?: TextPiece[];
  /** Per-line height (rich text lines can be taller than the base line height). */
  height?: number;
  /** Offset of the first character of this line in the (case-transformed) string. */
  start: number;
  /** Paragraph index (lines of a wrapped paragraph share it). */
  para?: number;
  /** Left indent of the paragraph (lists, nesting); included in x. */
  indent?: number;
  width: number;
  x: number; // offset within box after alignment
  y: number; // top of line box
  baseline: number; // y of the baseline
}

export interface TextLayout {
  lines: TextLine[];
  width: number; // max line width
  height: number; // total height
  style: TextStyle;
  font: string;
  /** Rich text: piece styles (0 = the base style). */
  styles?: PieceStyle[];
}

/** Advance width of a string including letter spacing (shared by layout code). */
export function measureString(s: string, font: string, ls: number, fx?: GlyphFeatures): number {
  return measureStr(s, font, ls, fx);
}

function measureStr(s: string, font: string, ls: number, fx?: GlyphFeatures): number {
  if (!s) return 0;
  // mixed directions: each run is shaped in its own direction
  if (shaper && RTL_CHARS.test(s)) {
    let w = 0;
    for (const r of visualRuns(s, bidiLevels(s), paragraphLevel(s))) w += measureRun(s.slice(r.start, r.end), font, ls, fx);
    return w;
  }
  return measureRun(s, font, ls, fx);
}

function measureRun(s: string, font: string, ls: number, fx?: GlyphFeatures): number {
  if (!s) return 0;
  const n = [...s].length;
  if (shaper) {
    const sh = shapeRun(s, font, fx);
    if (sh) return sh.width + ls * n;
  }
  if (fx && (fx.tnum || fx.liga === false)) {
    let w = 0;
    if (fx.liga === false) for (const ch of s) w += fx.tnum && DIGIT.test(ch) ? tabularCell(font) : measure(ch, font);
    else
      for (const seg of s.match(/[0-9]+|[^0-9]+/g) ?? []) w += DIGIT.test(seg[0]) ? seg.length * tabularCell(font) : measure(seg, font);
    return w + ls * n;
  }
  return measure(s, font) + ls * n;
}

/**
 * Lay out text. maxWidth = Infinity means no wrapping.
 * boxWidth/boxHeight are used for alignment (pass the node box).
 */
export function layoutText(
  characters: string,
  style: TextStyle,
  maxWidth: number,
  boxWidth?: number,
  boxHeight?: number,
  indents?: number[],
): TextLayout {
  const font = cssFont(style);
  const text = transformText(characters, style);
  const ls = style.letterSpacing;
  const fx = style.features;
  const raw: { text: string; start: number; width: number; para?: number; indent?: number }[] = [];
  let offset = 0;
  text.split('\n').forEach((para, pi) => {
    const ind = indents?.[pi] ?? 0;
    const from = raw.length;
    if (!Number.isFinite(maxWidth) || maxWidth <= 0) {
      raw.push({ text: para, start: offset, width: measureStr(para, font, ls, fx) });
    } else {
      wrapParagraph(para, offset, font, ls, Math.max(1, maxWidth - ind), raw, fx);
    }
    for (let k = from; k < raw.length; k++) (raw[k].para = pi), (raw[k].indent = ind);
    offset += para.length + 1;
  });
  // right-to-left and mixed lines: pieces in visual order (each in one direction)
  const paras = text.split('\n');
  const paraStart: number[] = [];
  paras.reduce((o, p, i) => ((paraStart[i] = o), o + p.length + 1), 0);
  const levelCache = new Map<number, [Uint8Array, 0 | 1]>();
  const pieceOf = (r: { text: string; start: number; para?: number }) => {
    const pi = r.para ?? 0;
    let lv = levelCache.get(pi);
    if (!lv) levelCache.set(pi, (lv = [bidiLevels(paras[pi], paragraphLevel(paras[pi])), paragraphLevel(paras[pi])]));
    const from = r.start - paraStart[pi];
    let x = 0;
    return visualRuns(r.text, lv[0].subarray(from, from + r.text.length), lv[1]).map((run) => {
      const t = r.text.slice(run.start, run.end);
      const w = measureRun(t, font, ls, fx);
      const piece: TextPiece = { text: t, style: 0, x, width: w, start: run.start, rtl: run.level % 2 === 1 };
      x += w;
      return piece;
    });
  };
  const bidiPieces = new Map<number, TextPiece[]>();
  raw.forEach((r, i) => {
    if (!RTL_CHARS.test(r.text)) return;
    const ps = pieceOf(r);
    bidiPieces.set(i, ps);
    r.width = ps.reduce((a, p) => a + p.width, 0);
  });
  const lh = style.lineHeight;
  const width = raw.reduce((m, l) => Math.max(m, l.width + (l.indent ?? 0)), 0);
  const height = raw.length * lh;
  const bw = boxWidth ?? width;
  const bh = boxHeight ?? height;
  const vOff = style.verticalAlign === 'CENTER' ? (bh - height) / 2 : style.verticalAlign === 'BOTTOM' ? bh - height : 0;
  // Baseline placement follows the CSS line-box model with Inter's hhea metrics
  // (ascender 0.969em, descender 0.242em), so DOM text editors line up exactly.
  const ascent = style.fontSize * ASCENT;
  const halfLeading = (lh - style.fontSize * (ASCENT + DESCENT)) / 2;
  const lines: TextLine[] = raw.map((l, i) => {
    const ind = l.indent ?? 0;
    const x = ind + (style.textAlign === 'CENTER' ? (bw - ind - l.width) / 2 : style.textAlign === 'RIGHT' ? bw - ind - l.width : 0);
    const y = vOff + i * lh;
    const pieces = bidiPieces.get(i);
    return { ...l, x, y, baseline: y + halfLeading + ascent, ...(pieces ? { pieces } : {}) };
  });
  return { lines, width, height, style, font };
}

function wrapParagraph(
  para: string,
  offset: number,
  font: string,
  ls: number,
  maxWidth: number,
  out: { text: string; start: number; width: number }[],
  fx?: GlyphFeatures,
) {
  if (para === '') {
    out.push({ text: '', start: offset, width: 0 });
    return;
  }
  // Tokenize into words with their trailing spaces.
  const tokens = para.match(/\S+\s*|\s+/g) ?? [para];
  let line = '';
  let lineStart = offset;
  let pos = offset;
  const flush = () => {
    const trimmed = line.replace(/\s+$/, '');
    out.push({ text: trimmed, start: lineStart, width: measureStr(trimmed, font, ls, fx) });
    line = '';
  };
  for (const tok of tokens) {
    if (line !== '' && measureStr((line + tok).trimEnd(), font, ls, fx) > maxWidth + 0.01) {
      flush();
      lineStart = pos;
    }
    if (line === '' && measureStr(tok.trimEnd(), font, ls, fx) > maxWidth + 0.01) {
      // Break a word that is wider than the box character by character.
      let chunk = '';
      for (const ch of tok) {
        if (chunk.trimEnd() && measureStr((chunk + ch).trimEnd(), font, ls, fx) > maxWidth + 0.01) {
          line = chunk;
          flush();
          lineStart = pos;
          chunk = '';
        }
        chunk += ch;
        pos += ch.length;
      }
      line = chunk;
      continue;
    }
    line += tok;
    pos += tok.length;
  }
  if (line !== '' || out.length === 0) flush();
}

export function listIndent(n: Pick<NodeData, 'fontSize'>): number {
  return Math.round((n.fontSize ?? 16) * 1.5);
}

export interface ResolvedParagraph {
  list: 'BULLET' | 'NUMBER' | null;
  level: number;
}

/** Effective format of every paragraph, or null when the text has no lists or indents at all. */
export function paragraphFormats(n: Pick<NodeData, 'characters' | 'listStyle' | 'paragraphs'>): ResolvedParagraph[] | null {
  if (!n.listStyle && !n.paragraphs?.some((p) => (p.list && p.list !== 'NONE') || p.level)) return null;
  const count = (n.characters ?? '').split('\n').length;
  const out: ResolvedParagraph[] = [];
  for (let i = 0; i < count; i++) {
    const p: ParagraphFormat = n.paragraphs?.[i] ?? {};
    const list = p.list === 'NONE' ? null : (p.list ?? n.listStyle ?? null);
    out.push({ list, level: Math.max(0, Math.min(8, p.level ?? 0)) });
  }
  return out;
}

function paragraphIndents(formats: ResolvedParagraph[], unit: number): number[] {
  return formats.map((f) => unit * (f.level + (f.list ? 1 : 0)));
}

const BULLETS = ['•', '◦', '▪'];
function roman(n: number): string {
  const t: [number, string][] = [[10, 'x'], [9, 'ix'], [5, 'v'], [4, 'iv'], [1, 'i']];
  let s = '';
  for (const [v, r] of t) while (n >= v) (s += r), (n -= v);
  return s;
}
function alpha(n: number): string {
  let s = '';
  while (n > 0) (s = String.fromCharCode(97 + ((n - 1) % 26)) + s), (n = Math.floor((n - 1) / 26));
  return s;
}

/** Bullets are drawn as shapes so they don't depend on the font having the glyphs. */
export function bulletShape(marker: string, x: number, baseline: number, fontSize: number): { kind: 'disc' | 'circle' | 'square'; cx: number; cy: number; r: number } | null {
  const kind = marker === '•' ? 'disc' : marker === '◦' ? 'circle' : marker === '▪' ? 'square' : null;
  if (!kind) return null;
  const r = fontSize * 0.15;
  return { kind, cx: x + r + fontSize * 0.05, cy: baseline - fontSize * 0.34, r };
}

/** Markers for list paragraphs: numbering restarts per level and whenever a run of numbered items is interrupted. */
export function listMarkers(formats: ResolvedParagraph[], paras: string[]): (string | null)[] {
  const counters: number[] = [];
  return formats.map((f, i) => {
    if (!f.list || !paras[i]?.length) {
      // an empty or plain paragraph at or above a level ends the numbering below it
      if (!f.list) counters.length = Math.min(counters.length, f.level);
      return null;
    }
    counters.length = Math.min(counters.length, f.level + 1);
    if (f.list === 'BULLET') {
      counters[f.level] = 0;
      return BULLETS[f.level % BULLETS.length];
    }
    const k = (counters[f.level] = (counters[f.level] ?? 0) + 1);
    const style = f.level % 3;
    return `${style === 0 ? k : style === 1 ? alpha(k) : roman(k)}.`;
  });
}

function applyMarkers(l: TextLayout, chars: string, formats: ResolvedParagraph[], unit: number) {
  const paras = transformText(chars, l.style).split('\n');
  const markers = listMarkers(formats, paras);
  const seen = new Set<number>();
  for (const line of l.lines) {
    const pi = line.para ?? 0;
    if (seen.has(pi)) continue;
    seen.add(pi);
    const m = markers[pi];
    if (!m) continue;
    line.marker = m;
    // the marker sits in the last indent step, bullets slightly inset
    line.markerX = line.x - unit + (formats[pi].list === 'BULLET' ? unit * 0.25 : 0);
  }
}

/** The most lines a truncating text node shows (Infinity when it doesn't truncate). */
export function truncatedLines(n: NodeData): number {
  if (n.textTruncation !== 'ENDING') return Infinity;
  const st = textStyle(n);
  const fit = n.textAutoResize === 'NONE' ? Math.max(1, Math.floor((n.height + 0.5) / st.lineHeight)) : Infinity;
  return Math.min(fit, n.maxLines && n.maxLines > 0 ? Math.floor(n.maxLines) : Infinity);
}

/** Bottom of each line, top to bottom. */
function lineBottoms(l: TextLayout): number[] {
  let y = 0;
  return l.lines.map((line) => (y += line.height ?? l.style.lineHeight));
}

/** How many lines a truncating text shows: its maxLines, and for a fixed box what fits in it. */
function lineLimit(n: Pick<NodeData, 'textTruncation' | 'maxLines' | 'textAutoResize' | 'height'>, l: TextLayout): number {
  if (n.textTruncation !== 'ENDING') return Infinity;
  let limit = n.maxLines && n.maxLines > 0 ? Math.floor(n.maxLines) : Infinity;
  if (n.textAutoResize === 'NONE') limit = Math.min(limit, Math.max(1, lineBottoms(l).filter((b) => b <= n.height + 0.5).length));
  return limit;
}

/** Cut text to `limit` lines, ending in an ellipsis (rich text keeps its runs up to the cut). */
function truncateText(chars: string, runs: TextRun[] | null, limit: number, lay: (chars: string, runs: TextRun[] | null) => TextLayout): TextLayout {
  const at = (k: number) => {
    const cut = chars.slice(0, k).trimEnd();
    // runs after the cut go; the run reaching it also styles the ellipsis
    const r = runs?.filter((x) => x.start < cut.length).map((x) => (x.end >= cut.length ? { ...x, end: cut.length + 1 } : x)) ?? null;
    return lay(cut + '…', r);
  };
  let lo = 0,
    hi = chars.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (at(mid).lines.length <= limit) lo = mid;
    else hi = mid - 1;
  }
  return at(lo);
}

/** Text layout for a node, using its current box for wrapping where appropriate. */
export function layoutNodeText(n: NodeData): TextLayout {
  const style = textStyle(n);
  const chars = n.characters ?? '';
  const runs = n.textRuns?.length ? n.textRuns : null;
  const formats = paragraphFormats(n);
  const unit = listIndent(n);
  const indents = formats ? paragraphIndents(formats, unit) : undefined;
  const lay = (max: number, bw?: number, bh?: number) => {
    const l = runs ? layoutRichText(chars, style, runs, max, bw, bh, indents) : layoutText(chars, style, max, bw, bh, indents);
    if (formats) applyMarkers(l, chars, formats, unit);
    return l;
  };
  if (n.type === 'TEXT') {
    const wrap = n.textAutoResize === 'WIDTH_AND_HEIGHT' ? Infinity : n.width;
    const l = lay(wrap, n.width, n.height);
    const limit = lineLimit(n, l);
    if (l.lines.length <= limit) return l;
    return truncateText(chars, runs, limit, (c, r) => {
      const t = r ? layoutRichText(c, style, r, wrap, n.width, n.height, indents) : layoutText(c, style, wrap, n.width, n.height, indents);
      if (formats) applyMarkers(t, c, formats, unit);
      return t;
    });
  }
  // Sticky / shape: its text box, always wraps
  const box = textBox(n);
  const l = lay(Math.max(1, box.width), box.width, box.height);
  // FigJam shapes cut text that doesn't fit and end it with an ellipsis
  if (n.type === 'SHAPE_WITH_TEXT' && !runs && !formats && chars && l.height > box.height + 0.5) {
    let lo = 0,
      hi = chars.length;
    while (lo < hi) {
      const mid = Math.ceil((lo + hi) / 2);
      const t = layoutText(chars.slice(0, mid).trimEnd() + '…', style, Math.max(1, box.width), box.width, box.height);
      if (t.height <= box.height + 0.5) lo = mid;
      else hi = mid - 1;
    }
    return layoutText(chars.slice(0, lo).trimEnd() + '…', style, Math.max(1, box.width), box.width, box.height);
  }
  return l;
}

// ---------------------------------------------------------------------------
// Rich text (style runs)
// ---------------------------------------------------------------------------

function runStyle(base: TextStyle, r: TextRun): TextStyle {
  return {
    ...base,
    fontFamily: r.fontFamily ?? base.fontFamily,
    fontSize: r.fontSize ?? base.fontSize,
    fontWeight: r.fontWeight ?? base.fontWeight,
    italic: r.italic ?? base.italic,
    letterSpacing: r.letterSpacing ?? base.letterSpacing,
    decoration: r.textDecoration ?? (r.link ? 'UNDERLINE' : base.decoration),
  };
}

/** Lay out text whose characters carry style runs. */
export function layoutRichText(characters: string, base: TextStyle, runs: TextRun[], maxWidth: number, boxWidth?: number, boxHeight?: number, indents?: number[]): TextLayout {
  const text = transformText(characters, base);
  const styles: (PieceStyle & { ts: TextStyle })[] = [{ font: cssFont(base), fontSize: base.fontSize, letterSpacing: base.letterSpacing, features: base.features, decoration: base.decoration, ts: base }];
  const styleAt = new Uint16Array(text.length);
  const autoLh = !(base.lineHeight && base.lineHeight !== Math.round(base.fontSize * 1.21 * 100) / 100);
  for (const r of runs) {
    const ts = runStyle(base, r);
    styles.push({ font: cssFont(ts), fontSize: ts.fontSize, letterSpacing: ts.letterSpacing, features: ts.features, decoration: ts.decoration, color: r.color, link: r.link, ts });
    const si = styles.length - 1;
    for (let i = Math.max(0, r.start); i < Math.min(text.length, r.end); i++) styleAt[i] = si;
  }
  // width of text[a, b) honoring styles
  const width = (a: number, b: number) => {
    let w = 0;
    let i = a;
    while (i < b) {
      const st = styleAt[i];
      let j = i + 1;
      while (j < b && styleAt[j] === st) j++;
      w += measureStr(text.slice(i, j), styles[st].font, styles[st].letterSpacing, styles[st].features);
      i = j;
    }
    return w;
  };
  const trimEnd = (a: number, b: number) => {
    while (b > a && /\s/.test(text[b - 1])) b--;
    return b;
  };
  // break into lines: [start, end)
  const lines: [number, number, number][] = [];
  let p0 = 0;
  let pi = -1;
  for (const para of text.split('\n')) {
    pi++;
    const pStart = p0,
      pEnd = p0 + para.length;
    const maxW = Math.max(1, maxWidth - (indents?.[pi] ?? 0));
    if (!Number.isFinite(maxWidth) || maxWidth <= 0 || para === '') lines.push([pStart, pEnd, pi]);
    else {
      const re = /\S+\s*|\s+/g;
      let m: RegExpExecArray | null;
      let ls = pStart,
        le = pStart;
      while ((m = re.exec(para))) {
        const ts = pStart + m.index,
          te = ts + m[0].length;
        if (le > ls && width(ls, trimEnd(ls, te)) > maxW + 0.01) {
          lines.push([ls, le, pi]);
          ls = ts;
        }
        // overlong word: break by characters
        if (le === ls && width(ts, trimEnd(ts, te)) > maxW + 0.01) {
          let cs = ts;
          for (let k = ts + 1; k <= te; k++) {
            if (k - cs > 1 && width(cs, trimEnd(cs, k)) > maxW + 0.01) {
              lines.push([cs, k - 1, pi]);
              cs = k - 1;
            }
          }
          ls = cs;
        }
        le = te;
      }
      lines.push([ls, le, pi]);
    }
    p0 = pEnd + 1;
  }
  const out: TextLine[] = [];
  let y = 0;
  let maxW = 0;
  const paraTexts = text.split('\n');
  const paraFrom: number[] = [];
  paraTexts.reduce((o, p, k) => ((paraFrom[k] = o), o + p.length + 1), 0);
  const paraLevels = new Map<number, [Uint8Array, 0 | 1]>();
  for (const [a, b0, para] of lines) {
    const b = trimEnd(a, b0);
    const pieces: TextPiece[] = [];
    let x = 0;
    let maxFs = base.fontSize;
    // style pieces of [from, to) in logical order
    const styled = (from: number, to: number) => {
      const out: [number, number][] = [];
      for (let i = from; i < to; ) {
        let j = i + 1;
        while (j < to && styleAt[j] === styleAt[i]) j++;
        out.push([i, j]);
        i = j;
      }
      return out;
    };
    const lineText = text.slice(a, b);
    if (RTL_CHARS.test(lineText)) {
      // bidirectional line: direction runs in visual order, split by style; right-to-left runs
      // show their styled parts right to left
      let lv = paraLevels.get(para);
      if (!lv) paraLevels.set(para, (lv = [bidiLevels(paraTexts[para], paragraphLevel(paraTexts[para])), paragraphLevel(paraTexts[para])]));
      const from = a - paraFrom[para];
      for (const run of visualRuns(lineText, lv[0].subarray(from, from + lineText.length), lv[1])) {
        const rtl = run.level % 2 === 1;
        const parts = styled(a + run.start, a + run.end);
        if (rtl) parts.reverse();
        for (const [i, j] of parts) {
          const w = width(i, j);
          pieces.push({ text: text.slice(i, j), style: styleAt[i], x, width: w, start: i - a, rtl });
          maxFs = Math.max(maxFs, styles[styleAt[i]].fontSize);
          x += w;
        }
      }
    } else
      for (const [i, j] of styled(a, b)) {
        const w = width(i, j);
        pieces.push({ text: text.slice(i, j), style: styleAt[i], x, width: w });
        maxFs = Math.max(maxFs, styles[styleAt[i]].fontSize);
        x += w;
      }
    const lh = autoLh ? Math.round(maxFs * 1.21 * 100) / 100 : Math.max(base.lineHeight, maxFs * 1.21 * (maxFs > base.fontSize ? 1 : 0));
    const ind = indents?.[para] ?? 0;
    out.push({ text: text.slice(a, b), start: a, width: x, x: 0, y, baseline: 0, pieces, height: lh, para, indent: ind });
    maxW = Math.max(maxW, x + ind);
    y += lh;
  }
  const height = y;
  const bw = boxWidth ?? maxW;
  const bh = boxHeight ?? height;
  const vOff = base.verticalAlign === 'CENTER' ? (bh - height) / 2 : base.verticalAlign === 'BOTTOM' ? bh - height : 0;
  for (const l of out) {
    const lh = l.height!;
    const fs = Math.max(base.fontSize, ...l.pieces!.map((p) => styles[p.style].fontSize));
    const ind = l.indent ?? 0;
    l.x = ind + (base.textAlign === 'CENTER' ? (bw - ind - l.width) / 2 : base.textAlign === 'RIGHT' ? bw - ind - l.width : 0);
    l.y += vOff;
    l.baseline = l.y + (lh - fs * (ASCENT + DESCENT)) / 2 + fs * ASCENT;
  }
  return { lines: out, width: maxW, height, style: base, font: styles[0].font, styles: styles.map(({ ts: _ts, ...rest }) => rest) };
}

/**
 * Keep paragraph formats attached to their paragraphs after an edit. Paragraphs merged by
 * deleting a line break keep the first one's format; new line breaks continue the format of the
 * paragraph they split (pressing Enter in a list makes another list item).
 */
export function adjustParagraphs(before: string, after: string, paras: ParagraphFormat[]): ParagraphFormat[] {
  let pre = 0;
  while (pre < before.length && pre < after.length && before[pre] === after[pre]) pre++;
  let suf = 0;
  while (suf < before.length - pre && suf < after.length - pre && before[before.length - 1 - suf] === after[after.length - 1 - suf]) suf++;
  const nl = (s: string) => (s.match(/\n/g) ?? []).length;
  let at = nl(before.slice(0, pre));
  const removed = nl(before.slice(pre, before.length - suf));
  const added = after.slice(pre, after.length - suf);
  const inserted = nl(added);
  // only line breaks typed right after a break: the diff can't tell "Enter at the end of the
  // previous paragraph" from "Enter at the start of this one"; the former is the common case
  if (pre > 0 && before[pre - 1] === '\n' && removed === 0 && /^\n+$/.test(added)) at--;
  const total = nl(after) + 1;
  const src = [...paras];
  while (src.length < nl(before) + 1) src.push({});
  const cur = src[at] ?? {};
  const out = [...src.slice(0, at + 1), ...Array.from({ length: inserted }, () => ({ ...cur })), ...src.slice(at + 1 + removed)];
  out.length = total;
  for (let i = 0; i < total; i++) out[i] ??= {};
  // drop trailing empty formats
  while (out.length && !out[out.length - 1].list && !out[out.length - 1].level) out.pop();
  return out;
}

/** Paragraph index containing character offset i. */
export function paragraphAt(text: string, i: number): number {
  let n = 0;
  for (let k = 0; k < Math.min(i, text.length); k++) if (text[k] === '\n') n++;
  return n;
}

/** Keep runs attached to the right characters after an edit from `before` to `after`. */
export function adjustRuns(before: string, after: string, runs: TextRun[]): TextRun[] {
  let pre = 0;
  while (pre < before.length && pre < after.length && before[pre] === after[pre]) pre++;
  let suf = 0;
  while (suf < before.length - pre && suf < after.length - pre && before[before.length - 1 - suf] === after[after.length - 1 - suf]) suf++;
  const oldEnd = before.length - suf; // edited region in old text: [pre, oldEnd)
  const newEnd = after.length - suf; // the same region in the new text: [pre, newEnd)
  const delta = after.length - before.length;
  const insertion = oldEnd === pre;
  const out: TextRun[] = [];
  for (const r of runs) {
    let { start, end } = r;
    if (start >= oldEnd && !(insertion && start === pre && end === pre)) {
      start += delta;
      end += delta;
    } else if (end < pre || (end === pre && !insertion)) {
      // entirely before the edit
    } else if (insertion && end === pre) {
      end += delta; // typing at the end of a run continues it
    } else {
      if (start > pre) start = newEnd;
      end = end >= oldEnd ? end + delta : newEnd;
    }
    start = Math.max(0, Math.min(after.length, start));
    end = Math.max(start, Math.min(after.length, end));
    if (end > start) out.push({ ...r, start, end });
  }
  return out;
}

/** Apply style props to [start, end), splitting / merging runs as needed. */
export function applyRunStyle(runs: TextRun[], length: number, start: number, end: number, props: Omit<TextRun, 'start' | 'end'>): TextRun[] {
  if (end <= start) return runs;
  // expand to per-character styles, then re-compress
  const per: (Omit<TextRun, 'start' | 'end'> | null)[] = new Array(length).fill(null);
  for (const r of runs) for (let i = Math.max(0, r.start); i < Math.min(length, r.end); i++) {
    const { start: _s, end: _e, ...st } = r;
    per[i] = { ...(per[i] ?? {}), ...st };
  }
  for (let i = start; i < Math.min(length, end); i++) {
    const merged: Record<string, unknown> = { ...(per[i] ?? {}), ...props };
    for (const k of Object.keys(merged)) if (merged[k] === undefined) delete merged[k];
    per[i] = Object.keys(merged).length ? (merged as Omit<TextRun, 'start' | 'end'>) : null;
  }
  const out: TextRun[] = [];
  for (let i = 0; i < length; ) {
    const st = per[i];
    let j = i + 1;
    while (j < length && JSON.stringify(per[j]) === JSON.stringify(st)) j++;
    if (st) out.push({ start: i, end: j, ...st });
    i = j;
  }
  return out;
}

/** Mind map topics hug their text with this padding. */
export const MIND_PADDING = 12;

/** Where a sticky's, shape's or mind-map topic's text goes, in node-local coordinates. */
export function textBox(n: Pick<NodeData, 'type' | 'width' | 'height'> & Partial<Pick<NodeData, 'mindParent' | 'mindMap' | 'shape'>>): { x: number; y: number; width: number; height: number } {
  if (n.type === 'SHAPE_WITH_TEXT' && !n.mindParent && !n.mindMap) return whiteboardTextBox(n.shape ?? 'SQUARE', n.width, n.height);
  const p = textPadding(n);
  return { x: p, y: p, width: Math.max(0, n.width - p * 2), height: Math.max(0, n.height - p * 2) };
}

export function textPadding(n: Pick<NodeData, 'type' | 'width' | 'height'> & Partial<Pick<NodeData, 'mindParent' | 'mindMap'>>): number {
  if (n.type === 'STICKY') return 20; // FigJam's (measured)
  if (n.mindParent || n.mindMap) return MIND_PADDING;
  if (n.type === 'SHAPE_WITH_TEXT') return Math.min(16, n.width * 0.1);
  return 0;
}

/** Natural size for a TEXT node given its auto-resize mode. */
export function measureTextNode(n: NodeData): { width: number; height: number } {
  // text on a path takes the path's box
  if (n.textPath) return { width: n.width, height: n.height };
  const style = textStyle(n);
  const chars = n.characters ?? '';
  const runs = n.textRuns?.length ? n.textRuns : null;
  const formats = paragraphFormats(n);
  const indents = formats ? paragraphIndents(formats, listIndent(n)) : undefined;
  const lay = (max: number) => (runs ? layoutRichText(chars, style, runs, max, undefined, undefined, indents) : layoutText(chars, style, max, undefined, undefined, indents));
  if (n.textAutoResize === 'WIDTH_AND_HEIGHT') {
    const l = lay(Infinity);
    return { width: Math.ceil(l.width * 100) / 100, height: Math.max(style.lineHeight, l.height) };
  }
  const l = lay(n.width);
  // auto-height text with maxLines stops growing at that many lines
  const limit = lineLimit({ ...n, textAutoResize: 'HEIGHT' }, l);
  const height = l.lines.length > limit ? lineBottoms(l)[limit - 1] : l.height;
  return { width: n.width, height: Math.max(style.lineHeight, height) };
}

/**
 * Sticky auto-shrink: the font size that makes the text fit inside the sticky.
 * Mirrors whiteboard tools, where sticky notes shrink text as you type.
 */
export function fitStickyFontSize(n: NodeData, base = 24, min = 8): number {
  const pad = textPadding(n);
  const w = n.width - pad * 2;
  const h = n.height - pad * 2;
  let size = base;
  while (size > min) {
    const l = layoutText(n.characters ?? '', textStyle({ ...n, fontSize: size, lineHeight: 0 }), w);
    if (l.height <= h && l.width <= w + 0.5) break;
    size -= size > 16 ? 2 : 1;
  }
  return size;
}

/** x of the character offset k within a line (relative to the box), honoring rich-text pieces. */
function lineCharX(layout: TextLayout, line: TextLine, k: number): number {
  if (line.pieces?.[0]?.start !== undefined) {
    // bidirectional line: the piece holding the character (or the one it ends)
    const p = line.pieces.find((q) => k >= q.start! && k < q.start! + q.text.length) ?? line.pieces.find((q) => k === q.start! + q.text.length);
    if (!p) return line.x;
    const st = layout.styles?.[p.style] ?? { font: layout.font, letterSpacing: layout.style.letterSpacing, features: layout.style.features };
    const w = measureStr(p.text.slice(0, k - p.start!), st.font, st.letterSpacing, st.features);
    return line.x + p.x + (p.rtl ? p.width - w : w);
  }
  if (!line.pieces || !layout.styles) return line.x + measureStr(line.text.slice(0, k), layout.font, layout.style.letterSpacing, layout.style.features);
  let off = 0;
  for (const p of line.pieces) {
    if (k <= off + p.text.length) {
      const st = layout.styles[p.style];
      return line.x + p.x + measureStr(p.text.slice(0, k - off), st.font, st.letterSpacing, st.features);
    }
    off += p.text.length;
  }
  const last = line.pieces[line.pieces.length - 1];
  return line.x + (last ? last.x + last.width : 0);
}

function lineHeightOf(layout: TextLayout, line: TextLine) {
  return line.height ?? layout.style.lineHeight;
}

/** Character index at a local point (for caret placement). */
export function hitTestText(layout: TextLayout, x: number, y: number): number {
  if (!layout.lines.length) return 0;
  let line = layout.lines[layout.lines.length - 1];
  for (const l of layout.lines) {
    if (y < l.y + lineHeightOf(layout, l)) {
      line = l;
      break;
    }
  }
  let best = 0;
  let bestD = Infinity;
  let idx = 0;
  for (const ch of [...line.text, '']) {
    const d = Math.abs(x - lineCharX(layout, line, idx));
    if (d < bestD) {
      bestD = d;
      best = idx;
    }
    idx += ch.length;
    if (!ch) break;
  }
  return line.start + best;
}

/** Caret position (local coords) for a character index. */
export function caretPosition(layout: TextLayout, index: number): { x: number; y: number; height: number } {
  const lh = layout.style.lineHeight;
  if (!layout.lines.length) return { x: 0, y: 0, height: lh };
  let line = layout.lines[0];
  for (const l of layout.lines) {
    if (index >= l.start) line = l;
  }
  const within = Math.min(index - line.start, line.text.length);
  return { x: lineCharX(layout, line, within), y: line.y, height: lineHeightOf(layout, line) };
}

/** Rectangles (local coords) covering the characters [a, b), one per line. */
export function selectionRects(layout: TextLayout, a: number, b: number): { x: number; y: number; width: number; height: number }[] {
  const out: { x: number; y: number; width: number; height: number }[] = [];
  if (b <= a) return out;
  layout.lines.forEach((l, i) => {
    const next = layout.lines[i + 1];
    const end = next ? next.start : l.start + l.text.length + 1;
    if (b <= l.start || a >= end) return;
    if (l.pieces?.[0]?.start !== undefined) {
      // bidirectional line: a selection can be several separate spans on screen
      const lo = a - l.start,
        hi = b - l.start;
      for (const p of l.pieces) {
        const s0 = Math.max(lo, p.start!),
          s1 = Math.min(hi, p.start! + p.text.length);
        if (s1 <= s0) continue;
        const xa = lineCharX(layout, l, s0),
          xb = s1 === p.start! + p.text.length ? l.x + p.x + (p.rtl ? 0 : p.width) : lineCharX(layout, l, s1);
        out.push({ x: Math.min(xa, xb), y: l.y, width: Math.max(1, Math.abs(xb - xa)), height: lineHeightOf(layout, l) });
      }
      return;
    }
    const x0 = lineCharX(layout, l, Math.max(0, a - l.start));
    const x1 = b >= end ? lineCharX(layout, l, l.text.length) + (next ? 4 : 0) : lineCharX(layout, l, Math.min(l.text.length, b - l.start));
    out.push({ x: x0, y: l.y, width: Math.max(1, x1 - x0), height: lineHeightOf(layout, l) });
  });
  return out;
}

/**
 * Underlines, from the bundled Inter's post table (underlinePosition −464, thickness 192 at 2816
 * units per em): the top of the line sits 0.165 em below the baseline, as Figma draws it.
 */
export const UNDERLINE_OFFSET = 0.165;
export const UNDERLINE_THICKNESS = 0.068;
